import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { toast } from 'sonner';
import { Link } from '@tanstack/react-router';
import {
  ArrowSquareOut as ArrowSquareOutIcon,
  CheckCircle as CheckCircleIcon,
  CircleNotch as CircleNotchIcon,
  DotsSixVertical as DotsSixVerticalIcon,
  Warning as WarningIcon,
} from '@phosphor-icons/react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import type {
  DiscountCandidateV2,
  DraftLineDiscountValidationV2,
  DraftLineStack,
} from '@erp/rpc';
import type { DiscountStackResult } from '@erp/shared-types';
import {
  DiscountStackValidationError,
  computeDiscountStack,
  orderElementsForStack,
} from '@erp/shared-utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import { Badge } from 'src/components/ui/badge';
import { formatCurrency } from 'src/pages/authenticated/sales-orders/components/line-item-table/priceUtils';
import {
  useApplyLineStackV2,
  useRemoveLineStackV2,
} from '@/api/rpc/hooks/accounts-receivable/useDraftDiscountsV2';
import { TooltipInfoIcon } from 'src/components/common/TooltipInfoIcon';
import { LineStatusChip } from './LineStatusChip';
import { statusToChip } from './statusToChip';
import { CreateLineDiscountModal } from './CreateLineDiscountModal';

function candidateValueLabel(
  c: DiscountCandidateV2,
  currency?: string | null,
): string {
  const value = Number(c.discountValue);
  switch (c.discountType) {
    case 'PERCENT_BPS_OFF':
      return `${(value / 100).toFixed(2)}% off`;
    case 'AMOUNT_OFF':
      return `${formatCurrency(value, currency)} off`;
    case 'FIXED_PRICE':
      return `${formatCurrency(value, currency)} fixed price`;
    default:
      return c.discountValue;
  }
}

/** One drag-sortable step row in the applied-discounts preview table. */
function SortableStackRow({
  id,
  dragDisabled,
  showHandle,
  children,
}: {
  id: string;
  /** True for FIXED_PRICE (pinned first) — row shows a spacer, not a handle. */
  dragDisabled: boolean;
  showHandle: boolean;
  children: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: dragDisabled });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center justify-between gap-1 border-b bg-background px-2 py-1 last:border-b-0',
        isDragging && 'relative z-10 opacity-70 shadow',
      )}
    >
      {showHandle &&
        (dragDisabled ? (
          <span className="w-3 shrink-0" aria-hidden />
        ) : (
          <button
            type="button"
            className="shrink-0 cursor-grab touch-none active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <DotsSixVerticalIcon size={12} className="text-muted-foreground" />
          </button>
        ))}
      {children}
    </div>
  );
}

/**
 * Per-line discount chip + management dialog (Discounts v2). The chip reflects
 * the line's validation status; clicking it opens a dialog with the price
 * facts, the discount stack, and a candidate picker. The stack shown is a
 * LIVE PREVIEW of the current checkbox selection + ordering (computed with the
 * same shared engine the backend uses) — nothing persists until Save Changes.
 * Replaces the v1 DiscountBadge on the review page.
 */
export function LineDiscountsDialog({
  draftId,
  lineId,
  validation,
  stack,
  candidates,
  currency,
  editable,
  customerId,
  productVariantId,
  sku,
  poUnitPrice,
  listPrice,
  orderDate,
  canCreateDiscount = false,
  open: controlledOpen,
  onOpenChange,
}: {
  draftId: string;
  lineId: string;
  validation: DraftLineDiscountValidationV2;
  stack: DraftLineStack | null;
  candidates: DiscountCandidateV2[];
  currency?: string | null;
  editable: boolean;
  customerId: string;
  productVariantId: string;
  sku: string;
  poUnitPrice: number | null;
  listPrice: number | null;
  orderDate?: string | null;
  canCreateDiscount?: boolean;
  /** Controlled open state (the review page lets the AI assistant open a line's dialog). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const t = useGT();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (controlledOpen === undefined) setUncontrolledOpen(next);
  };
  const [createOpen, setCreateOpen] = useState(false);
  const applyStack = useApplyLineStackV2(draftId);
  const removeStack = useRemoveLineStackV2(draftId);

  // Element data for everything selectable: candidates + whatever is already
  // applied (an applied element may have expired out of the candidate list).
  const elementById = new Map<string, DiscountCandidateV2>();
  for (const el of stack?.elements ?? []) elementById.set(el.id, el);
  for (const c of candidates) elementById.set(c.id, c);

  /** The persisted stack's element ids, in application order. */
  const appliedIds = (stack?.trace ?? [])
    .slice()
    .sort((a, b) => a.applicationOrder - b.applicationOrder)
    .map((s) => s.elementId);

  // Tentative selection + order — what the preview shows and Save persists.
  const [orderedIds, setOrderedIds] = useState<string[]>(appliedIds);

  // Re-seed the tentative selection whenever the persisted stack changes from
  // outside this dialog's Save (e.g. the create-discount modal applies the new
  // element) — otherwise the preview would keep showing the stale selection.
  const appliedKey = JSON.stringify(appliedIds);
  const [seenAppliedKey, setSeenAppliedKey] = useState(appliedKey);
  if (appliedKey !== seenAppliedKey) {
    setSeenAppliedKey(appliedKey);
    setOrderedIds(appliedIds);
  }

  /** Canonical order for a tentative id list (FIXED_PRICE hoisted first). */
  function normalize(ids: string[]): string[] {
    const inputs = ids
      .map((id) => elementById.get(id))
      .filter((el): el is DiscountCandidateV2 => !!el)
      .map((el) => ({
        id: el.id,
        discountType: el.discountType,
        discountValue: el.discountValue,
      }));
    return orderElementsForStack(inputs).map((el) => el.id);
  }

  // Live preview of the tentative stack, computed with the shared engine so
  // it matches what the backend will persist byte-for-byte.
  let preview: DiscountStackResult | null = null;
  let previewError: string | null = null;
  let previewErrorElementId: string | null = null;
  if (orderedIds.length > 0 && listPrice != null) {
    try {
      preview = computeDiscountStack({
        listUnitPrice: String(listPrice),
        quantity: 1,
        elements: orderedIds
          .map((id) => elementById.get(id))
          .filter((el): el is DiscountCandidateV2 => !!el)
          .map((el, index) => ({
            id: el.id,
            discountType: el.discountType,
            discountValue: el.discountValue,
            applicationOrder: index,
          })),
      });
    } catch (err) {
      if (err instanceof DiscountStackValidationError) {
        // Engine messages carry enum tokens — humanize them for display.
        previewError =
          err.code === 'MULTIPLE_FIXED_PRICE'
            ? t('Only one fixed price discount can be applied')
            : err.message
                .replaceAll('FIXED_PRICE', 'fixed price')
                .replaceAll('PERCENT_BPS_OFF', 'percent-off')
                .replaceAll('AMOUNT_OFF', 'amount-off');
        previewErrorElementId = err.elementId ?? null;
      } else {
        previewError = t('This combination cannot be computed');
      }
    }
  }
  const previewTrace = preview?.trace ?? [];
  const previewFinal =
    preview?.finalUnitPrice != null ? Number(preview.finalUnitPrice) : null;
  const remainingGap =
    previewFinal != null && poUnitPrice != null
      ? previewFinal - poUnitPrice
      : null;

  const baseChip = statusToChip(validation.status);
  // NO_MISMATCH covers at-or-above list; call out the above-list case so the
  // healthy-margin lines are visible at a glance.
  const chip =
    baseChip == null &&
    validation.status === 'NO_MISMATCH' &&
    poUnitPrice != null &&
    listPrice != null &&
    poUnitPrice - listPrice > 0.009
      ? 'above'
      : baseChip;
  const difference =
    poUnitPrice != null && listPrice != null ? listPrice - poUnitPrice : null;
  const showPriceFacts = difference != null && difference > 0.009;
  const priceResolved =
    validation.status === 'COVERED' || validation.status === 'PENDING_APPROVAL';
  const offerCreate = editable && canCreateDiscount && poUnitPrice != null;
  const dirty = JSON.stringify(orderedIds) !== JSON.stringify(appliedIds);
  const busy = applyStack.isPending || removeStack.isPending;

  function toggle(elementId: string) {
    setOrderedIds((prev) =>
      normalize(
        prev.includes(elementId)
          ? prev.filter((id) => id !== elementId)
          : [...prev, elementId],
      ),
    );
  }

  // Matched list order: currently-applied discounts first (stable within each
  // group so the list doesn't reshuffle while toggling checkboxes).
  const sortedCandidates = [...candidates].sort(
    (a, b) =>
      (appliedIds.includes(a.id) ? 0 : 1) - (appliedIds.includes(b.id) ? 0 : 1),
  );

  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  /** Drag-to-reorder a preview step (normalized so FIXED_PRICE stays first). */
  function handleStackDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedIds((prev) => {
      const from = prev.indexOf(String(active.id));
      const to = prev.indexOf(String(over.id));
      if (from < 0 || to < 0) return prev;
      return normalize(arrayMove(prev, from, to));
    });
  }

  // The dialog stays open after every action so users can keep adjusting.
  function save() {
    if (orderedIds.length === 0) {
      if (stack) clear();
      return;
    }
    applyStack.mutate(
      {
        draftSalesOrderId: draftId,
        draftLineId: lineId,
        elementIds: orderedIds,
      },
      {
        onSuccess: () => toast.success(t('Discounts applied')),
        onError: (err) =>
          toast.error(
            err instanceof Error
              ? err.message
              : t('Could not apply the discounts'),
          ),
      },
    );
  }

  function clear() {
    removeStack.mutate(
      { draftSalesOrderId: draftId, draftLineId: lineId },
      {
        onSuccess: () => {
          toast.success(t('Discounts removed'));
          setOrderedIds([]);
        },
        onError: () => toast.error(t('Could not remove the discounts')),
      },
    );
  }

  // No mismatch and not above list → the neutral v2-style "No Discount" pill.
  // Matched candidates may still exist; clicking opens the dialog that lists
  // them (the hover title keeps that affordance discoverable).
  const trigger = <LineStatusChip status={chip ?? 'none'} />;

  return (
    <>
      <button
        type="button"
        className="cursor-pointer"
        title={
          chip == null && candidates.length > 0
            ? t('Discounts available — click to view')
            : undefined
        }
        onClick={() => {
          setOrderedIds(appliedIds);
          setOpen(true);
        }}
      >
        {trigger}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-lg"
          // Don't autofocus the first control (the info-tooltip button) —
          // focusing it pops the tooltip open the moment the dialog opens.
          // Base UI's spelling of Radix's `onOpenAutoFocus` + `preventDefault`.
          initialFocus={false}
        >
          <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
            <DialogTitle>
              <T>Line Discounts</T>
            </DialogTitle>
            <DialogDescription>
              <T context="dialog description naming the order line being discounted">
                Discounts applied to product: <Var>{sku}</Var>
              </T>
            </DialogDescription>
            {showPriceFacts && (
              <div className="flex items-center gap-2">
                {priceResolved ? (
                  <CheckCircleIcon size={16} className="text-green-600" />
                ) : (
                  <WarningIcon size={16} className="text-red-600" />
                )}
                <span className="text-sm font-medium">
                  {priceResolved ? (
                    <T context="popover heading when the below-list PO price is explained by applied discounts">
                      Resolved Price Difference
                    </T>
                  ) : (
                    <T context="popover heading when the PO price is below the list price without an approved discount">
                      Unresolved Price Difference
                    </T>
                  )}
                </span>
              </div>
            )}
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 pt-1 pb-4">
            {showPriceFacts && (
              <div>
                <div className="mb-1 text-sm font-medium">
                  <T context="header for the price summary section of the line discounts dialog">
                    Summary
                  </T>
                </div>
                <div className="rounded border text-xs">
                  {poUnitPrice != null && (
                    <div className="flex items-center justify-between border-b px-2 py-1 last:border-b-0">
                      <span className="text-muted-foreground">
                        <T context="label in discount popover showing the PO price">
                          PO Price
                        </T>
                      </span>
                      <span className="font-medium tabular-nums">
                        {formatCurrency(poUnitPrice, currency)}
                      </span>
                    </div>
                  )}
                  {listPrice != null && (
                    <div className="flex items-center justify-between border-b px-2 py-1 last:border-b-0">
                      <span className="text-muted-foreground">
                        <T context="label in discount popover showing the list price">
                          List Price
                        </T>
                      </span>
                      <span className="font-medium tabular-nums">
                        {formatCurrency(listPrice, currency)}
                      </span>
                    </div>
                  )}
                  {difference != null && difference > 0 && (
                    <div
                      className={cn(
                        'flex items-center justify-between border-b px-2 py-1 font-medium last:border-b-0',
                        priceResolved ? 'text-green-700' : 'text-red-600',
                      )}
                    >
                      <span>
                        <T context="label in discount popover showing the price difference">
                          Difference
                        </T>
                      </span>
                      <span className="tabular-nums">
                        {formatCurrency(difference, currency)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="mb-1 flex items-center gap-1 text-sm font-medium">
                <T context="header for the applied discount stack math">
                  Applied Discounts
                </T>
                <TooltipInfoIcon
                  ariaLabel={t('How applied discounts work')}
                  description={
                    t(
                      'Discounts apply top to bottom. Each step starts from the price the previous step produced, so the order changes the math (reorder with the arrows).',
                    ) +
                    '\n\n' +
                    t(
                      'A fixed price discount is always first because it replaces the PO price outright. The line clears review only when the final unit price here exactly matches the PO price. The discounts have to explain the price the customer is actually paying.',
                    )
                  }
                />
                {dirty && (
                  <Badge variant="outline" className="ml-2">
                    <T context="badge marking the discount stack as an unsaved preview">
                      Preview
                    </T>
                  </Badge>
                )}
                {!dirty && stack?.pending && (
                  <Badge variant="outline" className="ml-2">
                    <T>Pending approval</T>
                  </Badge>
                )}
              </div>
              {previewError ? (
                <div className="space-y-1">
                  <p className="rounded border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
                    {previewError}
                  </p>
                  {/* The tentative stack, uncomputable as-is: offending
                      element in red; totals dashed until it's fixed. */}
                  <div className="rounded border text-xs">
                    {orderedIds.map((id) => {
                      const el = elementById.get(id);
                      if (!el) return null;
                      const offending = id === previewErrorElementId;
                      return (
                        <div
                          key={id}
                          className={cn(
                            'flex items-center justify-between gap-1 border-b px-2 py-1 last:border-b-0',
                            offending && 'text-red-600',
                          )}
                        >
                          <span
                            className={cn(
                              !offending && 'text-muted-foreground',
                            )}
                          >
                            {candidateValueLabel(el, currency)}
                          </span>
                          <span className="tabular-nums">—</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-between border-b px-2 py-1 font-medium">
                      <span>
                        <T>Final unit price</T>
                      </span>
                      <span className="tabular-nums">—</span>
                    </div>
                    <div className="flex items-center justify-between px-2 py-1 font-medium">
                      <span>
                        <T context="label showing the gap between the discounted price and the PO price">
                          Remaining Gap
                        </T>
                      </span>
                      <span className="tabular-nums">—</span>
                    </div>
                  </div>
                </div>
              ) : previewTrace.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  <T context="placeholder when the line has no applied discounts">
                    None applied
                  </T>
                </p>
              ) : (
                <div className="rounded border text-xs">
                  <DndContext
                    sensors={dragSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleStackDragEnd}
                  >
                    <SortableContext
                      items={previewTrace.map((s) => s.elementId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {previewTrace.map((step) => {
                        // FIXED_PRICE is pinned first (it overwrites the
                        // running price); other steps reorder freely below it.
                        const isFixed = step.discountType === 'FIXED_PRICE';
                        const reorderable = editable && previewTrace.length > 1;
                        return (
                          <SortableStackRow
                            key={step.elementId}
                            id={step.elementId}
                            dragDisabled={isFixed || !reorderable}
                            showHandle={reorderable}
                          >
                            <span className="text-muted-foreground">
                              {isFixed
                                ? t('Fixed price')
                                : step.discountType === 'PERCENT_BPS_OFF'
                                  ? `${(Number(step.discountValue) / 100).toFixed(2)}% ${t('off')}`
                                  : `−${formatCurrency(Number(step.discountValue), currency)}`}
                            </span>
                            <span className="ml-auto tabular-nums">
                              {formatCurrency(
                                Number(step.unitPriceBefore),
                                currency,
                              )}{' '}
                              →{' '}
                              {formatCurrency(
                                Number(step.unitPriceAfter),
                                currency,
                              )}
                            </span>
                          </SortableStackRow>
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                  <div className="flex items-center justify-between border-b px-2 py-1 font-medium">
                    <span>
                      <T>Final unit price</T>
                    </span>
                    <span className="tabular-nums">
                      {previewFinal != null
                        ? formatCurrency(previewFinal, currency)
                        : '—'}
                    </span>
                  </div>
                  {remainingGap != null && (
                    <div
                      className={cn(
                        'flex items-center justify-between px-2 py-1 font-medium',
                        Math.abs(remainingGap) <= 0.009
                          ? 'text-green-700'
                          : 'text-red-600',
                      )}
                    >
                      <span>
                        <T context="label showing the gap between the discounted price and the PO price">
                          Remaining Gap
                        </T>
                      </span>
                      <span className="tabular-nums">
                        {formatCurrency(remainingGap, currency)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="mb-1 flex items-center gap-1 text-sm font-medium">
                <T context="header for the discount candidates that apply to a line">
                  Matched Discounts
                </T>
                <TooltipInfoIcon
                  ariaLabel={t('What matched discounts are')}
                  description={t(
                    'Eligible discounts whose product target, customer, and effective window match this line. Check or uncheck them to preview the impact in the applied discounts above — nothing changes until you hit Save Changes.',
                  )}
                />
                {offerCreate && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto h-6 px-2 text-[11px] font-medium tracking-normal"
                    onClick={() => setCreateOpen(true)}
                  >
                    <T context="button label to create a discount for this order line">
                      Create New Discount
                    </T>
                  </Button>
                )}
              </div>
              {candidates.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  <T context="placeholder when no discounts match the line">
                    None matched
                  </T>
                </p>
              ) : (
                <div className="max-h-56 space-y-1 overflow-y-auto">
                  {sortedCandidates.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-start gap-2 rounded border px-2 py-1.5 text-xs hover:bg-accent/50"
                    >
                      <Checkbox
                        className="mt-0.5"
                        disabled={!editable}
                        checked={orderedIds.includes(c.id)}
                        onCheckedChange={() => toggle(c.id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium">
                          {candidateValueLabel(c, currency)}
                        </span>
                        {c.pending && (
                          <Badge variant="outline" className="ml-1">
                            <T>Pending</T>
                          </Badge>
                        )}
                        {c.groupName && (
                          <span className="block text-[11px] text-muted-foreground">
                            <span className="font-semibold">
                              <T context="label for the discount group name on a matched discount row">
                                Title:
                              </T>
                            </span>{' '}
                            {c.groupName}
                          </span>
                        )}
                        <span className="block text-[11px] text-muted-foreground">
                          <span className="font-semibold">
                            <T context="label for the product target on a matched discount row">
                              Product:
                            </T>
                          </span>{' '}
                          {c.targetLabel ?? c.targetValue ?? t('All products')}
                        </span>
                        {c.effectiveEnd && (
                          <span className="block text-[11px] text-muted-foreground">
                            <span className="font-semibold">
                              <T context="label for the expiry date on a matched discount row">
                                Valid until:
                              </T>
                            </span>{' '}
                            {c.effectiveEnd}
                          </span>
                        )}
                        {c.isSingleUse && (
                          <span className="block text-[11px] text-muted-foreground">
                            <span className="font-semibold">
                              <T context="label for the single-use flag on a matched discount row">
                                Single use:
                              </T>
                            </span>{' '}
                            <T context="value of the single-use flag">Yes</T>
                          </span>
                        )}
                        {c.maxQuantity != null && (
                          <span className="block text-[11px] text-muted-foreground">
                            <span className="font-semibold">
                              <T context="label for the quantity cap on a matched discount row">
                                Quantity limit:
                              </T>
                            </span>{' '}
                            {`${Number(c.usedQuantity)} / ${Number(c.maxQuantity)}`}{' '}
                            <T context="suffix after a used/max quantity fraction">
                              units used
                            </T>
                          </span>
                        )}
                        {c.endCustomerName && (
                          <span className="block text-[11px] text-muted-foreground">
                            <span className="font-semibold">
                              <T context="label for the end customer on a matched discount row">
                                End Customer:
                              </T>
                            </span>{' '}
                            {c.endCustomerName}
                          </span>
                        )}
                      </span>
                      {c.groupId && (
                        <Link
                          to="/discounts/$groupId"
                          params={{ groupId: c.groupId }}
                          target="_blank"
                          title={t('Open this discount on the Discounts page')}
                          className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                          <ArrowSquareOutIcon size={14} />
                        </Link>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          {editable && (
            <DialogFooter className="shrink-0 border-t px-6 py-4">
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                disabled={busy || !stack}
                onClick={clear}
              >
                <T context="button that removes all applied discounts from the line">
                  Clear Applied
                </T>
              </Button>
              <Button
                size="sm"
                disabled={busy || !dirty || !!previewError}
                onClick={save}
              >
                {busy ? (
                  <CircleNotchIcon size={14} className="animate-spin" />
                ) : (
                  <T>Save Changes</T>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {createOpen && poUnitPrice != null && (
        <CreateLineDiscountModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          draftId={draftId}
          lineId={lineId}
          customerId={customerId}
          productVariantId={productVariantId}
          sku={sku}
          poUnitPrice={poUnitPrice}
          listPrice={listPrice}
          orderDate={orderDate}
          currency={currency}
        />
      )}
    </>
  );
}
